import React, { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { TabMenuBar } from '@octane/spark'
import { useBrandConfig } from 'contexts/BrandConfigContextProvider'
import { useFeatureFlagContext } from 'contexts/FeatureFlagContextProvider'
import useUser from 'hooks/useUser'
import DashboardPage from 'pages/Dashboard'
import LoadingPage from 'pages/LoadingPage/LoadingPage'

import FlexLink from './FlexLink'
import getHomeLink from './getHomeLink'
import {
    Container,
    SectionNews,
    TabContainer,
    TabContent,
    TabLabel,
} from './Home.styled'
import { toTabItems } from './toTabItems'
import { ASSET_TYPE_TO_TAB_PATH, FramePath, HomeTabPath } from './types'

export default function Home(): JSX.Element {
    const navigate = useNavigate()
    const location = useLocation()
    const featureFlags = useFeatureFlagContext()
    const brandExperienceConfig = useBrandConfig()
    const { assetTypes, isLoading, isRVDealership, user } = useUser()
    const flexLink = user?.dealership?.flexLink
    const redirectTo = location.state?.redirectTo

    // Handle redirect after login
    useEffect(() => {
        if (redirectTo) {
            navigate(redirectTo, { replace: true, state: {} })
        }
    }, [redirectTo, navigate])

    const showSectionFlexLink =
        flexLink && featureFlags?.showFlexLink && !isRVDealership

    const framePaths: FramePath[] = toTabItems(
        brandExperienceConfig.newsPathUrl?.trim(),
        assetTypes ?? [],
        user?.dealership?.isAgreementSignedWithHnb,
        featureFlags?.enableHnbIntegration,
        brandExperienceConfig.isDefault,
    )

    const primaryAssetType = user?.dealership?.primaryAssetType

    const primaryPath = primaryAssetType
        ? ASSET_TYPE_TO_TAB_PATH[primaryAssetType]
        : undefined

    const primaryTabIndex = framePaths.findIndex(
        (framePath) => framePath.path === primaryPath,
    )

    // Move the primary-asset tab to the front so it is selected and shown first.
    // If it is already first, or there is no match, keep the original order.
    const reorderedFramePaths: FramePath[] =
        primaryTabIndex > 0
            ? [
                  framePaths[primaryTabIndex],
                  ...framePaths.slice(0, primaryTabIndex),
                  ...framePaths.slice(primaryTabIndex + 1),
              ]
            : framePaths

    const [userSelectedPath, setUserSelectedPath] =
        React.useState<HomeTabPath | null>(null)

    const selectedPath =
        userSelectedPath ?? reorderedFramePaths?.[0]?.path ?? 'news'

    // TODO: We have a wide iframe and a narrow one in the dashboard,
    // so we should be able to configure both with the branded experience URLs

    // show dashboard instead of the news iframe
    // TODO: Handle both default 'auto-dealer-news' and branded auto paths
    const showDashboard = selectedPath === 'auto-dealer-news'

    // Show loading while user data is loading or while redirecting
    if (isLoading || redirectTo) {
        return <LoadingPage />
    }

    return (
        <Container>
            {showSectionFlexLink && (
                <FlexLink dealership={user?.dealership} flexLink={flexLink} />
            )}
            <TabContainer>
                {reorderedFramePaths.length > 1 &&
                    reorderedFramePaths.map(({ icon, name, path }) => (
                        <TabContent key={path}>
                            <TabMenuBar>
                                <TabMenuBar.Item
                                    active={path === selectedPath}
                                    onClick={() => setUserSelectedPath(path)}
                                >
                                    <TabLabel icon={icon}>{name}</TabLabel>
                                </TabMenuBar.Item>
                            </TabMenuBar>
                        </TabContent>
                    ))}
            </TabContainer>
            <SectionNews isRVDealer={isRVDealership}>
                {showDashboard ? (
                    <DashboardPage />
                ) : (
                    <iframe
                        className="main-news-panel"
                        name="news-panel-iframe"
                        sandbox="allow-downloads allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox allow-forms"
                        src={getHomeLink(user, selectedPath)}
                        title="News"
                    />
                )}
            </SectionNews>
        </Container>
    )
}
